import { useState } from "react";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ChevronLeft, BookOpen, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { GrammarEditor } from "@/components/language/GrammarEditor";
import { Dictionary } from "@/components/language/Dictionary";
import { ScriptTab } from "@/components/language/Script";
import { ImageGallery } from "@/components/ImageGallery";
import { api, errMsg } from "@/lib/api";
import { useLanguage, useInvalidate } from "@/lib/queries";

function NameEditor({ lang, canEdit }) {
  const invalidate = useInvalidate();
  const [name, setName] = useState(lang.name);
  const save = async () => {
    if (name.trim() === lang.name || !name.trim()) return setName(lang.name);
    try {
      await api.patch(`/languages/${lang.id}`, { name });
      invalidate(["language", lang.id], ["languages", lang.universe_id], ["entities", lang.universe_id]);
      toast.success("Language renamed");
    } catch (e) {
      toast.error(errMsg(e));
      setName(lang.name);
    }
  };
  if (!canEdit) return <h1 className="font-display text-5xl sm:text-6xl text-[#F3EBDD] leading-none" data-testid="language-name">{lang.name}</h1>;
  return <Input value={name} onChange={(e) => setName(e.target.value)} onBlur={save} onKeyDown={(e) => e.key === "Enter" && e.target.blur()}
    className="h-20 border-0 bg-transparent px-0 font-display text-5xl md:text-6xl text-[#F3EBDD] shadow-none focus-visible:ring-0" data-testid="language-name" />;
}

export default function LanguagePage() {
  const { lid } = useParams();
  const { canEdit, base } = useOutletContext();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const { data: lang, isError } = useLanguage(lid);
  const [confirm, setConfirm] = useState(false);
  if (isError) return <p className="p-12 text-[#A8A090]" data-testid="language-not-found">This language no longer exists.</p>;
  if (!lang) return <p className="p-12 text-[#A8A090]">Loading language…</p>;

  const remove = async () => {
    await api.delete(`/languages/${lid}`);
    invalidate(["languages", lang.universe_id], ["entities", lang.universe_id]);
    toast.success(`${lang.name} deleted`);
    navigate(`${base}/languages`);
  };

  return (
    <div className="max-w-5xl px-4 sm:px-8 lg:px-14 py-10">
      <button onClick={() => navigate(`${base}/languages`)} className="flex items-center gap-1 text-xs text-[#A8A090] hover:text-[#F3EBDD] mb-8" data-testid="language-back-button">
        <ChevronLeft className="h-3.5 w-3.5" />Languages
      </button>
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="lf-label text-[#14B8A6] mb-2">Language · {lang.word_count} words</div>
          <NameEditor key={lang.name} lang={lang} canEdit={canEdit} />
        </div>
        <div className="flex gap-1 pt-6">
          {lang.entity_id && <Button variant="ghost" onClick={() => navigate(`${base}/lore/${lang.entity_id}`)} data-testid="language-lore-link"><BookOpen className="h-4 w-4 mr-1.5" />Lore entry</Button>}
          {canEdit && <Button size="icon" variant="ghost" onClick={() => setConfirm(true)} data-testid="language-delete-button"><Trash2 className="h-4 w-4 text-red-400" /></Button>}
        </div>
      </div>
      <Tabs defaultValue="dictionary" className="mt-8">
        <TabsList data-testid="language-tabs">
          <TabsTrigger value="dictionary" data-testid="language-tab-dictionary">Dictionary</TabsTrigger>
          <TabsTrigger value="grammar" data-testid="language-tab-grammar">How it works</TabsTrigger>
          <TabsTrigger value="script" data-testid="language-tab-script">Script</TabsTrigger>
          <TabsTrigger value="images" data-testid="language-tab-images">Images</TabsTrigger>
        </TabsList>
        <TabsContent value="dictionary" className="mt-6"><Dictionary lang={lang} canEdit={canEdit} /></TabsContent>
        <TabsContent value="grammar" className="mt-6"><GrammarEditor key={lang.updated_at} lang={lang} canEdit={canEdit} /></TabsContent>
        <TabsContent value="script" className="mt-6"><ScriptTab lang={lang} canEdit={canEdit} /></TabsContent>
        <TabsContent value="images" className="mt-2">
          <ImageGallery images={lang.images || []} canEdit={canEdit} title={`${lang.name} images`} onSave={async (images) => {
            await api.put(`/languages/${lang.id}/images`, { images });
            invalidate(["language", lang.id]);
          }} />
        </TabsContent>
      </Tabs>
      <ConfirmDialog open={confirm} onOpenChange={setConfirm} onConfirm={remove} title={`Delete ${lang.name}?`}
        description="Its dictionary will be deleted. The Lore Bible entry is kept." />
    </div>
  );
}
